import { FileButton, Loader, LoadingOverlay } from '@mantine/core'
import { IconBan, IconCrop, IconMoon, IconRestore, IconSun, IconTexture, IconTrash, IconUpload } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import { ImageCropDialog } from '../assets/ImageCropDialog'
import { Segmented } from '../v2/Segmented'
import { ThumbCarousel } from './ThumbCarousel'
import { V2Collapse, V2ColorField, V2SliderField, V2Toggle } from './V2Fields'
import { ImageWarning, usePickedTile } from './ImagePanel'
import { useImageMediaField } from '../../fields/useImageMediaField'
import { proxiedImageUrl, useAdvertiserLibrary } from '../../fields/useAdvertiserLibrary'
import { getAtPath } from '../../utils/configPaths'
import { matchesCondition, visibleOptions } from '../../utils/conditions'
import { fieldLabel, offeredOptions } from '../../utils/uiLayoutV2'
import classes from './V2Form.module.css'

const MODE_LABELS = { logo: 'editorV2.form.brand.modeLogo', favicon: 'editorV2.form.brand.modeFavicon', none: 'editorV2.form.brand.modeNone' }
// The pill colour each tone paints by default (template.css), shown while no colour is picked.
const TONE_FILLS = { light: '#ffffff', dark: '#0f0f0f' }
// The favicon background suggests the prototype's neutrals, not the ad palette.
const FILL_SWATCHES = ['#c4c4c4', '#6e6e6e', '#9c9c9c', '#2a2a2a', '#3d3d3d', '#a3a3a3', '#1f1f1f']
const TONE_ICONS = {
  light: <IconSun size={18} stroke={1.6} />,
  dark: <IconMoon size={18} stroke={1.6} />,
  none: <IconBan size={18} stroke={1.6} />,
}

/**
 * Left: the preview card (Figma 250×254: 234×200 image, Replace / Crop / Add Background / delete). A favicon
 * is shown the way the ad shows it — in the white brand pill over the picture; a logo sits
 * on a transparency checkerboard. Right top: "Advertiser Library" with the Upload tile,
 * 64px logo tiles and "Import Logos from Website".
 */
function LogoPanel({ field, fieldProps, brandName, pillName, plate, children }) {
  const { t } = useTranslation()
  const {
    media, target, accept, sourceUrl, previewUrl, cropMode, cropUseful, imageWarning,
    localError, busy, cropOpened, setCropOpened, chooseFile, adoptUrl, applyCrop, clear,
  } = useImageMediaField(fieldProps)
  const library = useAdvertiserLibrary({
    configuration: fieldProps.configuration,
    assetSourceUrls: fieldProps.assetSourceUrls,
    logos: true,
  })
  const picked = usePickedTile(media.sourceAssetId)
  const currentUrl = media.sourceAssetId ? fieldProps.configuration?.assets?.[media.sourceAssetId]?.url : null
  const favicon = field.path.endsWith('.favicon')
  // The logo is placed whole: the box shows the picture itself, not the small rendition the save
  // draws at the ad's logo size (which read as if another, smaller logo had been saved).
  const shown = sourceUrl || previewUrl

  return (
    <>
      <div className={classes.identityMain}>
        <div className={classes.previewCard}>
          <div className={classes.previewImage} data-checker={!favicon && shown ? 'true' : undefined}>
            <LoadingOverlay visible={busy} zIndex={5} overlayProps={{ blur: 1 }} loaderProps={{ color: '#d00b65', size: 'sm' }} />
            <ImageWarning messages={[imageWarning]} />
            {favicon ? (
              <FaviconMock src={shown} name={brandName} pillName={pillName} />
            ) : shown ? (
              <img src={shown} alt="" />
            ) : (
              <FileButton accept={accept} onChange={chooseFile}>
                {(buttonProps) => (
                  <button {...buttonProps} type="button" className={classes.dropButton} style={{ fontSize: 14 }}>
                    <IconUpload size={20} stroke={1.6} />
                    <span>{t('editorV2.form.brand.uploadLogo')}</span>
                  </button>
                )}
              </FileButton>
            )}
          </div>
          <div className={classes.previewActions}>
            <div>
              <FileButton accept={accept} onChange={chooseFile}>
                {(buttonProps) => (
                  <button {...buttonProps} type="button" className={classes.smallButton}>
                    <IconUpload size={14} stroke={1.8} />
                    {t('editorV2.form.common.replace')}
                  </button>
                )}
              </FileButton>
              {cropUseful && sourceUrl && (
                <button type="button" className={classes.smallButton} onClick={() => setCropOpened(true)}>
                  <IconCrop size={14} stroke={1.8} />
                  {t('editorV2.form.common.crop')}
                </button>
              )}
              {plate && (
                <button
                  type="button"
                  className={classes.smallButton}
                  data-active={plate.value ? 'true' : undefined}
                  aria-pressed={Boolean(plate.value)}
                  onClick={() => plate.onChange(!plate.value)}
                >
                  <IconTexture size={14} stroke={1.8} />
                  {t('editorV2.form.brand.addBackground')}
                </button>
              )}
            </div>
            <button
              type="button"
              className={classes.iconButton}
              aria-label={t('editorV2.form.common.removeLabel', { label: fieldLabel(field) })}
              disabled={!media.sourceAssetId}
              onClick={clear}
            >
              <IconTrash size={14} stroke={1.8} />
            </button>
          </div>
        </div>
      </div>

      <div className={classes.identitySide}>
        <div className={classes.stack}>
          <span className={classes.label}>{t('editorV2.form.common.advertiserLibrary')}</span>
          <div className={classes.library}>
            <div className={classes.libraryTiles}>
              <FileButton accept={accept} onChange={chooseFile}>
                {(buttonProps) => (
                  <button {...buttonProps} type="button" className={classes.logoTile} aria-label={t('editorV2.form.brand.uploadLabel', { label: fieldLabel(field) })}>
                    <IconUpload size={14} stroke={1.8} color="#212529" />
                    <span>{t('editorV2.form.common.upload')}</span>
                  </button>
                )}
              </FileButton>
              {library.images.map((item) => {
                const selected = picked.isPicked(item, currentUrl)
                return (
                  <button
                    key={item.url}
                    type="button"
                    className={classes.logoTile}
                    data-selected={selected ? 'true' : 'false'}
                    aria-pressed={selected}
                    title={item.url}
                    onClick={() => {
                      if (selected) return
                      picked.begin(item.url)
                      adoptUrl(item.url, undefined, item).then((imported) => { if (!imported) picked.cancel() })
                    }}
                  >
                    <img src={proxiedImageUrl(item)} alt="" loading="lazy" />
                  </button>
                )
              })}
              {library.loading && <Loader size="xs" color="#d00b65" m="auto 8px" />}
            </div>
            {!library.loading && !library.images.length && (
              <span className={classes.libraryEmpty}>
                {library.error || (library.website ? t('editorV2.form.brand.noLogosFound') : t('editorV2.form.brand.addWebsiteForLogos'))}
              </span>
            )}
            <button
              type="button"
              className={classes.pinkButton}
              disabled={!library.website || library.loading}
              onClick={library.reload}
            >
              {t('editorV2.form.brand.importLogos')}
            </button>
          </div>
        </div>
        {children}
        {(localError || fieldProps.error) && <p className={classes.error}>{localError || fieldProps.error}</p>}
      </div>

      {cropMode && target && (
        <ImageCropDialog
          opened={cropOpened}
          onClose={() => setCropOpened(false)}
          onApply={applyCrop}
          sourceUrl={sourceUrl}
          crop={media.crop}
          target={target}
          label={fieldLabel(field)}
        />
      )}
    </>
  )
}

// AD-252: `pillName` is the ad's pill text (`data.brand.displayName`, else the brand name), edited here.
function FaviconMock({ src, name, pillName }) {
  const { t } = useTranslation()
  return (
    <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #d8d2cc, #eee7e0 45%, #cfc6bd)' }}>
      <span style={{ display: 'flex', alignItems: 'center', gap: 8, maxWidth: 222, padding: '12px 16px', borderRadius: 16, background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,.08)' }}>
        <span style={{ display: 'inline-flex', width: 32, height: 32, flex: 'none', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', borderRadius: 34, background: '#f1f3f5' }}>
          {src ? <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} /> : null}
        </span>
        {pillName ? (
          <input
            className={classes.pillName}
            value={pillName.value}
            placeholder={name || t('editorV2.form.brand.yourBrand')}
            size={Math.max(4, (pillName.value || name || t('editorV2.form.brand.yourBrand')).length)}
            maxLength={80}
            aria-label={t('editorV2.form.brand.pillName')}
            title={t('editorV2.form.brand.pillName')}
            onChange={(event) => pillName.onChange(event.currentTarget.value)}
          />
        ) : (
          <span style={{ overflow: 'hidden', fontSize: 10, fontWeight: 500, lineHeight: '15px', color: '#000', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {name || t('editorV2.form.brand.yourBrand')}
          </span>
        )}
      </span>
    </span>
  )
}

/**
 * Figma Identity block: "Identity" segment (Logo / Favicon / No Logo, 420×40) and, unless
 * No Logo, the bordered panel with the preview, the library, Background (favicon), Position
 * and the logo-size slider (logo).
 */
export function BrandIdentity({ modeField, fields, shared, errors }) {
  const { t } = useTranslation()
  const { configuration, updateValue } = shared
  const mode = getAtPath(configuration, modeField.path)
  const byPath = Object.fromEntries(fields.map((field) => [field.path, field]))
  const visible = (field) => field && matchesCondition(field.visibleWhen, configuration)
  const imageField = fields.find((field) => field.type === 'image-editor' && visible(field))
  const tone = fields.find((field) => field.ui?.control === 'tone-segmented')
  const position = fields.find((field) => field.path.endsWith('.alignment'))
  const size = fields.find((field) => field.ui?.control === 'percentage-slider')
  const margin = fields.find((field) => field.path.endsWith('.logoMargin'))
  const plate = fields.find((field) => field.path.endsWith('.logoPlate'))
  const fill = fields.find((field) => field.path.endsWith('.faviconFill'))
  const fillColor = fields.find((field) => field.path.endsWith('.faviconColor'))
  const brandName = getAtPath(configuration, 'data.brand.name')
  const displayName = getAtPath(configuration, 'data.brand.displayName')
  const pillName = typeof displayName === 'string'
    ? { value: displayName, onChange: (next) => updateValue('data.brand.displayName', next) }
    : null

  const propsFor = (field) => ({
    field,
    path: field.path,
    value: getAtPath(configuration, field.path),
    error: errors[field.path],
    onChange: (next) => updateValue(field.path, next),
    configuration,
    templateManifest: shared.templateManifest,
    addAsset: shared.addAsset,
    addRenditionPreview: shared.addRenditionPreview,
    assetUrls: shared.assetUrls,
    assetSourceUrls: shared.assetSourceUrls,
  })

  const pickedFill = fillColor ? getAtPath(configuration, fillColor.path) || '' : ''
  const toneFill = (TONE_FILLS[getAtPath(configuration, 'data.brand.tone')] || getAtPath(configuration, 'data.colors.c2') || TONE_FILLS.dark).toLowerCase()
  const pickFill = (next) => {
    const stored = next.toLowerCase() === toneFill ? '' : next
    if (stored.toLowerCase() !== pickedFill.toLowerCase()) updateValue(fillColor.path, stored)
  }
  // AD-252: one favicon Background — Bright, Dark or None (the "With background" switch off).
  const toneShown = visible(tone) && byPath[tone.path]
  const fillOff = visible(fill) && getAtPath(configuration, fill.path) === false
  const toneCurrent = toneShown ? getAtPath(configuration, tone.path) : null
  const toneOptions = toneShown ? [
    ...offeredOptions(tone, visibleOptions(tone.options, configuration), toneCurrent),
    ...(visible(fill) ? [{ value: 'none', label: t('editorV2.form.brand.toneNone') }] : []),
  ] : []
  const pickTone = (next) => {
    if (next === 'none') {
      updateValue(fill.path, false)
      return
    }
    if (fillOff) updateValue(fill.path, true)
    if (next !== toneCurrent) updateValue(tone.path, next)
  }

  const fillControls = ((!toneShown && visible(fill)) || visible(fillColor)) && (
    <div className={classes.identityFill}>
      {!toneShown && visible(fill) && <V2Toggle {...propsFor(fill)} />}
      {visible(fillColor) && (
        <>
          <V2ColorField
            {...propsFor(fillColor)}
            label={toneShown ? t('editorV2.form.brand.color') : t('editorV2.form.brand.background')}
            value={(pickedFill || toneFill).toUpperCase()}
            onChange={pickFill}
            palette={FILL_SWATCHES}
          />
          {pickedFill && (
            <div className={classes.identityReset}>
              <button type="button" className={classes.smallButton} onClick={() => updateValue(fillColor.path, '')}>
                <IconRestore size={14} stroke={1.8} />
                {t('editorV2.form.brand.resetDefault')}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )

  const modeOptions = offeredOptions(modeField, visibleOptions(modeField.options, configuration), mode)

  return (
    <div className={`${classes.identity} ${classes.box}`}>
      <span className={classes.label}>{fieldLabel(modeField)}</span>
      <Segmented
        ariaLabel={fieldLabel(modeField)}
        size="lg"
        value={mode}
        onChange={(next) => updateValue(modeField.path, next)}
        className={classes.identitySegment}
        options={modeOptions.map((option) => ({ value: option.value, label: MODE_LABELS[option.value] ? t(MODE_LABELS[option.value]) : option.label }))}
      />
      {errors[modeField.path] && <p className={classes.error}>{errors[modeField.path]}</p>}

      {imageField && (
        <div className={classes.identityPanel}>
          <LogoPanel
            key={imageField.path}
            field={imageField}
            fieldProps={propsFor(imageField)}
            brandName={brandName}
            pillName={pillName}
            plate={visible(plate) ? propsFor(plate) : null}
          >
            <V2Collapse title={t('editorV2.form.brand.settings', { label: fieldLabel(imageField) })} bodyClassName={classes.collapseStack}>
              {toneShown && (
                <div className={classes.stack}>
                  <span className={classes.label}>{fieldLabel(tone)}</span>
                  <Segmented
                    ariaLabel={fieldLabel(tone)}
                    value={fillOff ? 'none' : toneCurrent}
                    onChange={pickTone}
                    className={classes.toneSegment}
                    options={toneOptions.map((option) => ({ value: option.value, label: option.label, icon: TONE_ICONS[option.value] || null }))}
                  />
                </div>
              )}
              {visible(position) && (
                <ThumbCarousel
                  field={position}
                  value={getAtPath(configuration, position.path)}
                  options={visibleOptions(position.options, configuration)}
                  onChange={(next) => updateValue(position.path, next)}
                  templateManifest={shared.templateManifest}
                  error={errors[position.path]}
                  heading={fieldLabel(position)}
                  compact
                />
              )}
              {visible(size) && <V2SliderField {...propsFor(size)} />}
              {visible(margin) && <V2Toggle {...propsFor(margin)} />}
              {fillControls}
            </V2Collapse>
          </LogoPanel>
        </div>
      )}
    </div>
  )
}
